import { CustomChip } from '@app/components/CustomChip';
import { Box, BoxProps, ChipProps } from '@mui/material';
import { ReactElement, ReactNode } from 'react';

/** One selectable entry in `ChipToggleGroup`; `chipColor` is used outlined at 0.8 opacity when off, filled when selected. */
export type ChipToggleOption = {
  value: string;
  label: ReactNode;
  /** Accent: outlined + 0.8 opacity when unselected, filled when selected. */
  chipColor: string;
  icon?: ReactElement;
  /** Passed to `CustomChip` when selected if contrast needs a fixed label/icon color. */
  filledLabelColor?: string;
  /** When true, the chip is not clickable. */
  disabled?: boolean;
};

type ChipToggleGroupBaseProps = {
  options: ChipToggleOption[];
  /** MUI chip size for every option. Default `small`. */
  chipSize?: ChipProps['size'];
  /** Styles for the flex row container. */
  sx?: BoxProps['sx'];
};

type ChipToggleGroupSingleRequiredProps = ChipToggleGroupBaseProps & {
  multiple?: false;
  /** Must match one of `options[].value`. */
  value: string;
  onChange: (next: string) => void;
};

export type ChipToggleGroupSingleProps = ChipToggleGroupSingleRequiredProps &
  (
    | {
        /** Clicking the selected chip is ignored. */
        allowDeselect?: false;
        deselectValue?: never;
      }
    | {
        /**
         * Clicking the selected chip goes back to `deselectValue`.
         * Clicking `deselectValue` when it is already selected is ignored.
         */
        allowDeselect: true;
        deselectValue: string;
      }
  );

export type ChipToggleGroupMultiProps = ChipToggleGroupBaseProps & {
  multiple: true;
  /** At least one id; toggling the last one off is ignored. */
  value: string[];
  onChange: (next: string[]) => void;
};

export type ChipToggleGroupProps = ChipToggleGroupSingleProps | ChipToggleGroupMultiProps;

/**
 * Controlled chip group for single or multi selection using `CustomChip`.
 * Single: one value; with `allowDeselect`, clicking the active chip returns to `deselectValue`.
 * Multi: at least one value; removing the last selected chip is ignored.
 */
export function ChipToggleGroup(props: ChipToggleGroupProps) {
  const { options, sx, chipSize = 'small' } = props;

  function handleClick(optionValue: string) {
    if (props.multiple === true) {
      const { value, onChange } = props;
      const isSelected = value.includes(optionValue);
      if (isSelected) {
        if (value.length <= 1) {
          return;
        }
        onChange(value.filter((v) => v !== optionValue));
        return;
      }
      onChange([...value, optionValue]);
      return;
    }

    const { value, onChange } = props;
    if (optionValue === value) {
      if (props.allowDeselect === true && optionValue !== props.deselectValue) {
        onChange(props.deselectValue);
      }
      return;
    }
    onChange(optionValue);
  }

  return (
    <Box
      sx={[
        { display: 'flex', flexWrap: 'wrap', gap: 1 },
        ...(sx == null ? [] : Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {options.map((option) => {
        const selected =
          props.multiple === true
            ? props.value.includes(option.value)
            : props.value === option.value;

        /** Multi: cannot deselect the last remaining chip — no click, no hover press animation, not-allowed cursor. */
        const lastSelectedLocked = props.multiple === true && selected && props.value.length <= 1;
        const locked = option.disabled === true || lastSelectedLocked;

        return (
          <CustomChip
            key={option.value}
            clickable={!locked}
            disabled={option.disabled}
            size={chipSize}
            variant={selected ? 'filled' : 'outlined'}
            chipColor={option.chipColor}
            filledLabelColor={selected ? option.filledLabelColor : undefined}
            icon={option.icon}
            label={option.label}
            onClick={locked ? undefined : () => handleClick(option.value)}
            sx={{
              '& .MuiChip-label': {
                typography: chipSize === 'medium' ? ('body2' as const) : ('caption' as const),
              },
              '& .MuiChip-icon': { color: 'currentColor' },
              ...(!selected ? { opacity: 0.7 } : {}),
              ...(lastSelectedLocked ? { cursor: 'not-allowed' } : {}),
            }}
          />
        );
      })}
    </Box>
  );
}
